/**
 * ContractorCard Component
 *
 * Card component displaying contractor information including:
 * - Category badge
 * - Profile photo
 * - Name, location, and hourly rate
 * - Rating with stars
 * - Completed tasks count
 * - View profile button
 */

import { forwardRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { cn } from '@/src/shared/lib/utils';
import { Avatar } from '@/src/shared/ui/avatars/Avatar';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { getLocalizedName, type Locale } from '@hummii/shared-types';

export interface ContractorCardProps {
  /**
   * Contractor ID
   */
  id: string;

  /**
   * User ID (for profile navigation)
   */
  userId: string;

  /**
   * Contractor name
   */
  name: string;

  /**
   * Profile photo URL
   */
  avatarUrl?: string | null;

  /**
   * Rating (0-5)
   */
  rating: number | null;

  /**
   * Hourly rate in dollars
   */
  hourlyRate: number | null;

  /**
   * City
   */
  city: string | null;

  /**
   * Province code (e.g., ON, QC)
   */
  province?: string | null;

  /**
   * Latitude coordinate
   */
  latitude?: number | null;

  /**
   * Longitude coordinate
   */
  longitude?: number | null;

  /**
   * Number of completed orders
   */
  completedOrders: number;

  /**
   * Is verified contractor
   */
  isVerified: boolean;

  /**
   * Categories (from shared-types structure)
   */
  categories: Array<{
    id: string;
    nameEn: string;
    nameFr: string;
  }> | null;

  /**
   * Click handler for view profile button
   */
  onViewProfile?: (id: string) => void;

  /**
   * Click handler for location icon (navigate to map marker)
   */
  onLocationClick?: (id: string) => void;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export const ContractorCard = forwardRef<HTMLDivElement, ContractorCardProps>(
  (
    {
      id,
      userId,
      name,
      avatarUrl,
      city,
      province,
      latitude,
      longitude,
      hourlyRate,
      rating,
      completedOrders,
      isVerified,
      categories,
      onViewProfile,
      onLocationClick,
      className,
    },
    ref
  ) => {
    const t = useTranslations('landing.contractorCard');
    const locale = useLocale() as Locale;
    const router = useRouter();

    const handleViewProfile = () => {
      if (onViewProfile) {
        onViewProfile(userId);
      } else {
        // Navigate to worker's public profile using userId
        router.push(`/${locale}/profile/worker/${userId}`);
      }
    };

    // Get primary category based on current locale
    const primaryCategory = categories?.[0]
      ? getLocalizedName(categories[0], locale)
      : 'Service';

    // Format location
    // Show city if available, otherwise show "Location set" if coordinates exist
    const hasCoordinates =
      typeof latitude === 'number' &&
      typeof longitude === 'number' &&
      !Number.isNaN(latitude) &&
      !Number.isNaN(longitude);
    const location = city
      ? `${city}${province ? ', ' + province : ''}`
      : hasCoordinates
      ? t('locationSet') // "Location set" when only coordinates are available
      : t('locationNotSpecified');

    // Render star icons based on rating
    const renderStars = () => {
      const stars = [];
      const numericRating = rating !== null ? Number(rating) : 0;
      const fullStars = Math.floor(numericRating);

      for (let i = 0; i < 5; i++) {
        stars.push(
          <svg
            key={i}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            className="inline-block"
          >
            <path
              d="M8 1.33334L9.88 5.14667L14 5.74667L11 8.66667L11.76 12.7733L8 10.78L4.24 12.7733L5 8.66667L2 5.74667L6.12 5.14667L8 1.33334Z"
              fill={i < fullStars ? '#6AB344' : '#E0E0E0'}
              stroke={i < fullStars ? '#6AB344' : '#E0E0E0'}
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      }
      return stars;
    };

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col w-full bg-background-primary rounded-[16px] overflow-hidden shadow-sm border border-[#E0E0E0]',
          className
        )}
      >
        {/* Category Badge */}
        <div className="w-full bg-[#6AB344] px-4 py-3 flex items-center justify-center">
          <Typography
            variant="body"
            weight="semibold"
            className="text-white text-center"
            truncate
          >
            {primaryCategory}
          </Typography>
        </div>

        {/* Main Content */}
        <div className="flex flex-col items-center px-4 pb-6">
          {/* Avatar */}
          <div className="mt-4 mb-3">
            <Avatar
              src={avatarUrl || undefined}
              alt={name}
              size="lg"
            />
          </div>

          {/* Name with location icon and verification badge */}
          <div className="flex items-center justify-center gap-1.5 mb-1">
            {/* Location icon on the left */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const hasValidCoords =
                  typeof latitude === 'number' &&
                  typeof longitude === 'number' &&
                  !Number.isNaN(latitude) &&
                  !Number.isNaN(longitude);
                if (hasValidCoords && onLocationClick) {
                  onLocationClick(id);
                }
              }}
              disabled={
                typeof latitude !== 'number' ||
                typeof longitude !== 'number' ||
                Number.isNaN(latitude) ||
                Number.isNaN(longitude) ||
                !onLocationClick
              }
              className={cn(
                "p-1 rounded-full transition-colors flex-shrink-0",
                typeof latitude === 'number' &&
                  typeof longitude === 'number' &&
                  !Number.isNaN(latitude) &&
                  !Number.isNaN(longitude) &&
                  onLocationClick
                  ? "hover:bg-gray-100 cursor-pointer"
                  : "opacity-30 cursor-not-allowed"
              )}
              title={
                typeof latitude === 'number' &&
                typeof longitude === 'number' &&
                !Number.isNaN(latitude) &&
                !Number.isNaN(longitude)
                  ? `Show on map (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`
                  : "Location not available"
              }
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                className="text-accent-primary"
              >
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                <circle
                  cx="12"
                  cy="9"
                  r="2.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
            </button>
            <Typography
              variant="h3"
              weight="bold"
              color="primary"
              className="text-center"
            >
              {name}
            </Typography>
            {/* Verified badge disabled (Identity Verification removed) */}
            {/* {isVerified && (
              <span className="text-accent-primary" title={t('verified')}>✓</span>
            )} */}
          </div>

          {/* Location and Rate */}
          <Typography
            variant="bodySm"
            color="secondary"
            className="mb-2 text-center"
          >
            {location} • {hourlyRate !== null ? `$${hourlyRate}${t('hourlyRate')}` : t('rateNotSet')}
          </Typography>

          {/* Rating */}
          {rating !== null && (
            <div className="flex items-center gap-1.5 mb-1">
              <Typography
                variant="body"
                weight="bold"
                className="text-[#6AB344]"
              >
                {Number(rating).toFixed(1)}
              </Typography>
              <div className="flex gap-0.5">
                {renderStars()}
              </div>
            </div>
          )}

          {/* Tasks Completed */}
          <Typography
            variant="bodySm"
            color="secondary"
            className="mb-4 text-center"
          >
            {completedOrders} {t('tasksCompleted', { count: completedOrders })}
          </Typography>

          {/* View Profile Button */}
          <button
            onClick={handleViewProfile}
            className="btn-link " >
            {t('viewProfile')}
          </button>
        </div>
      </div>
    );
  }
);

ContractorCard.displayName = 'ContractorCard';
